/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import { useLocation } from 'react-router-dom';

import type { TypeActe, Mention, Registre } from '@/types';
import { ROLES_PARENTS_PAR_TYPE_ACTE } from '@/constants';
import { AssistantContext } from '@/contexts';
import type { AssistantContextType } from '@/contexts/AssistantContext';

interface AssistantContextProviderProps {
  children: React.ReactNode;
}

const AssistantContextProvider = ({
  children,
}: AssistantContextProviderProps) => {
  const location = useLocation();

  // Le type d'acte est transmis à l'assistant via le paramètre d'URL `type`
  // (ex. /assistant?type=naissance) depuis la modale de saisie.
  const typeFromUrl = React.useMemo(
    () =>
      (new URLSearchParams(location.search).get('type') as TypeActe | null) ||
      null,
    [location.search],
  );

  const [selectedTypeActe, setSelectedTypeActe] =
    React.useState<TypeActe | null>(typeFromUrl);
  const [data, setData] = React.useState<Record<string, any>>({});
  const [selectedRegistre, setSelectedRegistre] = React.useState<
    Partial<Registre> | undefined
  >();
  const [mentions, setMentions] = React.useState<Mention[]>([]);
  const [errors, setErrors] = React.useState<Record<string, any>>({});

  React.useEffect(() => {
    setSelectedTypeActe(typeFromUrl);
  }, [typeFromUrl]);

  /**
   * Préremplit la civilité des parents quand le type d'acte l'impose par le
   * rôle (reconnaissance : père / mère). Le champ est alors verrouillé dans
   * `StepParents`, donc l'agent ne peut pas le corriger : la valeur doit être
   * posée ici, sinon `formatSexe` renverrait « femme » par défaut pour le père.
   *
   * ⚠️ Écrit uniquement si la civilité est absente : ce `useEffect` se
   * redéclenche sur un assistant déjà monté (le type vient de `location.search`)
   * et écraserait sans ça une saisie en cours. Aucun effet pour un type d'acte
   * hors du registre — la naissance garde une civilité libre et vide.
   *
   * Se redéclenche aussi quand la civilité disparaît : cocher « Parent
   * inconnu » vide tout le parent (`ParentForm` de la lib), et la décocher
   * doit rétablir la civilité du rôle (AD-12860). Rien n'est posé tant que le
   * parent reste inconnu.
   */
  const sexeParent1 = data?.parent1?.sexe;
  const sexeParent2 = data?.parent2?.sexe;
  const parent1Inconnu = Boolean(data?.filiationInconnueParent1);
  const parent2Inconnu = Boolean(data?.filiationInconnueParent2);

  // biome-ignore lint/correctness/useExhaustiveDependencies: déclencheurs seuls, l'état est relu dans `setData`
  React.useEffect(() => {
    const roles = typeFromUrl
      ? ROLES_PARENTS_PAR_TYPE_ACTE[typeFromUrl]
      : undefined;

    if (!roles) {
      return;
    }

    setData((prev) => {
      const manquants = ([1, 2] as const).filter(
        (numero) =>
          _.isEmpty(prev?.[`parent${numero}`]?.sexe) &&
          !prev?.[`filiationInconnueParent${numero}`],
      );

      if (_.isEmpty(manquants)) {
        return prev;
      }

      const suivant = _.cloneDeep(prev);

      for (const numero of manquants) {
        _.set(suivant, `parent${numero}.sexe`, roles[numero].sexe);
      }

      return suivant;
    });
  }, [typeFromUrl, sexeParent1, sexeParent2, parent1Inconnu, parent2Inconnu]);

  const handleDataChange = React.useCallback((name: string, value: unknown) => {
    setData((prev) => {
      const newData = _.cloneDeep(prev);

      _.set(newData, name, value);

      return newData;
    });
  }, []);

  const handleChange = React.useCallback(
    (
      _e: React.SyntheticEvent | null,
      {
        name,
        value,
        checked,
      }: { name: string; value?: unknown; checked?: boolean },
    ) => {
      handleDataChange(name, checked ?? value);
    },
    [handleDataChange],
  );

  const handleReset = React.useCallback(() => {
    setSelectedTypeActe(null);
    setData({});
    setSelectedRegistre(undefined);
    setMentions([]);
    setErrors({});
  }, []);

  const value = React.useMemo<AssistantContextType>(
    () => ({
      selectedTypeActe,
      setSelectedTypeActe,
      data,
      onChange: handleChange,
      selectedRegistre,
      setSelectedRegistre,
      mentions,
      setMentions,
      errors,
      setErrors,
      onReset: handleReset,
    }),
    [
      selectedTypeActe,
      data,
      handleChange,
      selectedRegistre,
      mentions,
      errors,
      handleReset,
    ],
  );

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
};

export default AssistantContextProvider;
